/**
 * Damage numbers (ADR 0004, "Schadensnummern").
 *
 * The database stores a plain INT per tenant, counted from 1 by
 * `dbo.next_damage_number`. Everything below is presentation: the reference a
 * person reads, says on the phone and types into the search is
 * `<CODE>-<NNNNNN>`, e.g. `FSV-000042`, where CODE is the tenant code.
 *
 * The number part is always exactly six characters, so lists line up in a
 * monospace column. Up to 999 999 it is digits with leading zeros. After that
 * a letter takes the first position and the remaining five count on:
 *
 *   1 000 000 → A00000,  1 000 001 → A00001,  … 1 099 999 → A99999,
 *   1 100 000 → B00000,  …                    3 599 999 → Z99999.
 *
 * 3 599 999 is the end; the database counter refuses the next number with the
 * same limit (ck_damage_counters_range).
 */

import { isValidTenantCode } from './tenant';

/** Width of the number part after the code. */
export const DAMAGE_NUMBER_WIDTH = 6;
/** The largest damage number of a tenant: Z99999. */
export const DAMAGE_NUMBER_MAX = 3_599_999;

const DIGITS_MAX = 999_999;
const LETTER_BLOCK = 100_000;
const LETTER_BASE = DIGITS_MAX + 1;

/** The six-character number part: 000042, A00001. Throws outside 1…DAMAGE_NUMBER_MAX. */
function numberPart(n: number): string {
  if (!Number.isInteger(n) || n < 1 || n > DAMAGE_NUMBER_MAX) {
    throw new RangeError(`Damage number out of range: ${n}`);
  }
  if (n <= DIGITS_MAX) return String(n).padStart(DAMAGE_NUMBER_WIDTH, '0');
  const offset = n - LETTER_BASE;
  const letter = String.fromCharCode(65 + Math.floor(offset / LETTER_BLOCK));
  return letter + String(offset % LETTER_BLOCK).padStart(DAMAGE_NUMBER_WIDTH - 1, '0');
}

/**
 * `formatDamageNumber(42, 'FSV')` → `FSV-000042`; without a code → `000042`.
 * Throws for a number outside 1…DAMAGE_NUMBER_MAX or a malformed code: a
 * reference that cannot be parsed back is worse than an error.
 */
export function formatDamageNumber(n: number, code?: string): string {
  const part = numberPart(n);
  if (code === undefined) return part;
  if (!isValidTenantCode(code)) throw new RangeError(`Invalid tenant code: ${code}`);
  return `${code}-${part}`;
}

export interface DamageReference {
  /** Upper-case tenant code, when the input named one. */
  readonly code?: string;
  readonly number: number;
}

// Optional "CODE-", then either a letter with exactly five digits or 1-7 digits.
const REFERENCE = /^(?:([A-Z]{2,5})\s*-\s*)?(?:([A-Z])(\d{5})|(\d{1,7}))$/;

/**
 * Reads what a person types as a damage reference: `FSV-000042`, `fsv-42`,
 * `000042`, `42`, `A00001`, `FSV-A00001`. Case and surrounding whitespace do
 * not matter. Returns null for anything else, including 0 and numbers beyond
 * DAMAGE_NUMBER_MAX. Whether a named code is the active tenant's is the
 * caller's question.
 */
export function parseDamageReference(input: string): DamageReference | null {
  const m = REFERENCE.exec(input.trim().toUpperCase());
  if (!m) return null;
  const [, code, letter, rest, digits] = m;
  const number =
    letter !== undefined && rest !== undefined
      ? LETTER_BASE + (letter.charCodeAt(0) - 65) * LETTER_BLOCK + Number(rest)
      : Number(digits);
  if (number < 1 || number > DAMAGE_NUMBER_MAX) return null;
  return code !== undefined ? { code, number } : { number };
}
